import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";
import TuneIcon from "@mui/icons-material/Tune";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { PlasticsQuestions } from "./questions";
import { PlasticsPlayBook } from "./playbook";
import ProgressiveAnalysisTracker from "../../fb/cookies/ProgressiveAnalysisTracker";
import { getTowerConfig } from "../../../utils/commodityConfig";
import { apiFetch } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { theme } from "../../../design/theme";

const { perlColors: assumptionColors, perlTypography } = theme.custom;

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total"]);
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  ohp: "Overhead & Profits",
};

const readValidatedPlasticsQuote = (data) => {
  if (data?.status !== "success" || !data?.ShouldCostResult) {
    throw new Error("A successful validated Plastics quote was not returned.");
  }
  const row = data.ShouldCostResult;
  const readCost = (field, optional = false) => {
    const value = row[field];
    if (optional && value === undefined) return 0;
    if (
      value === null || value === undefined || typeof value === "boolean" ||
      (typeof value !== "number" && typeof value !== "string") ||
      (typeof value === "string" && !value.trim())
    ) {
      throw new Error(`Missing or invalid ${field} in Plastics quote.`);
    }
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0) {
      throw new Error(`Missing or invalid ${field} in Plastics quote.`);
    }
    return number;
  };
  const material = readCost("MaterialCost");
  const process = readCost("ProcessCost");
  const ohp = readCost("OHPCost");
  const packaging = readCost("PackagingCost", true);
  const total = readCost("TotalCost");
  const componentTotal = material + process + ohp + packaging;
  if (!Number.isFinite(componentTotal) ||
      Math.abs(total - componentTotal) > 0.0002 + Math.abs(componentTotal) * 1e-12) {
    throw new Error("Plastics quote components do not reconcile with its total.");
  }
  return {
    breakdown: { material, process, ohp, ...(packaging !== 0 ? { packaging } : {}),
      total_should_cost: total, currency: "USD/unit" },
    total,
  };
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => ({
    key,
    label: BREAKDOWN_LABELS[key] || toLabel(key),
    value: Number(value) || 0,
  }));
};

const normalizePlasticsProductData = (data) => {
  const source = data && typeof data === "object" ? data : {};
  const partWeightDimensions =
    source.partWeightDimensions && typeof source.partWeightDimensions === "object"
      ? source.partWeightDimensions
      : {};
  const partDetails =
    source.partDetails && typeof source.partDetails === "object" ? source.partDetails : {};

  return {
    ...source,
    productName: source.productName || "",
    materialSelection:
      source.materialGrade ||
      source.materialSelection?.material ||
      source.materialSelection?.name ||
      source.materialSelection ||
      "",
    materialGrade: source.materialGrade || "",
    batchSize: source.batchSize || "",
    manufacturingCountry: source.manufacturingCountry || source.country || "",
    partWeightDimensions: {
      finishWeight: partWeightDimensions.finishWeight || "",
      partLength: partWeightDimensions.partLength || "",
      partWidth: partWeightDimensions.partWidth || "",
      partHeight: partWeightDimensions.partHeight || "",
    },
    partDetails: {
      regrindPct: partDetails.regrindPct || "",
      avgWallThickness: partDetails.avgWallThickness || partDetails.wallThickness || "",
      runnerType: partDetails.runnerType || "",
      noOfCavities: partDetails.noOfCavities || "",
      machineTonnage: partDetails.machineTonnage || "",
    },
  };
};

const areProductDataEqual = (left, right) =>
  JSON.stringify(left || null) === JSON.stringify(right || null);

const buildAssumptionsArray = ({
  assumptionsRaw,
  productData,
  formulaResult,
  materialPricePerKg,
  cycleTimeSeconds,
  machineRatePerHour,
}) => {
  let assumptionsArray = [];

  if (Array.isArray(assumptionsRaw)) {
    assumptionsArray = assumptionsRaw;
  } else if (assumptionsRaw && typeof assumptionsRaw === "object") {
    Object.values(assumptionsRaw).forEach((section) => {
      if (Array.isArray(section)) {
        assumptionsArray.push(...section);
      }
    });
  }

  if (materialPricePerKg !== null && !Number.isNaN(materialPricePerKg) && materialPricePerKg > 0) {
    const hasMaterialPrice = assumptionsArray.some((assumption) => {
      const assumptionStr = typeof assumption === "string" ? assumption : String(assumption || "");
      return assumptionStr.toLowerCase().includes("material price:");
    });

    if (!hasMaterialPrice) {
      assumptionsArray.push(`Material price: $${materialPricePerKg.toFixed(2)}/kg`);
    }
  }

  if (cycleTimeSeconds > 0) {
    const hasCycleTime = assumptionsArray.some((assumption) => {
      const assumptionStr = typeof assumption === "string" ? assumption : String(assumption || "");
      return (
        assumptionStr.toLowerCase().includes("cycle time") ||
        assumptionStr.toLowerCase().includes("cycle time(sec):")
      );
    });

    if (!hasCycleTime) {
      assumptionsArray.push(`Cycle Time(sec): ${cycleTimeSeconds.toFixed(2)}`);
    }
  }

  const requiredTonnage =
    formulaResult?.MachineTonnage?.required_ton ??
    formulaResult?.MachineTonnage?.requiredTonnage_ton;
  if (requiredTonnage !== undefined && requiredTonnage !== null) {
    const hasMachineTonnage = assumptionsArray.some((assumption) => {
      const assumptionStr = typeof assumption === "string" ? assumption : String(assumption || "");
      return (
        assumptionStr.toLowerCase().startsWith("machine tonnage:") ||
        assumptionStr.toLowerCase().startsWith("tonnage:")
      );
    });

    if (!hasMachineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${parseFloat(requiredTonnage).toFixed(2)} tons`);
    }
  }

  if (machineRatePerHour !== null && !Number.isNaN(machineRatePerHour) && machineRatePerHour > 0) {
    const hasMachineRate = assumptionsArray.some((assumption) => {
      const assumptionStr = typeof assumption === "string" ? assumption : String(assumption || "");
      return assumptionStr.toLowerCase().includes("machine rate:");
    });

    if (!hasMachineRate) {
      assumptionsArray.push(`Machine rate: $${machineRatePerHour.toFixed(2)}/hour`);
    }
  }

  if (assumptionsArray.length === 0 && productData) {
    const dimensionData = productData?.partWeightDimensions || {};
    if (
      dimensionData.partLength &&
      dimensionData.partWidth &&
      dimensionData.partHeight &&
      dimensionData.finishWeight
    ) {
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`,
      );
    }
    if (productData.materialSelection) {
      assumptionsArray.push(`Material: ${productData.materialSelection}`);
    }
    if (productData.batchSize) {
      assumptionsArray.push(`Batch Size: ${productData.batchSize}`);
    }
  }

  // Ensure the render layer can overwrite these values from formula output when present.
  void formulaResult;

  return assumptionsArray;
};

function renderAssumptions({ assumptions = [], productData, formulaResult }) {
  if (!assumptions || assumptions.length === 0) {
    return null;
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          bgcolor: assumptionColors.surface,
          border: `1px solid ${assumptionColors.border}`,
          borderRadius: 2,
          mt: 3,
        }}
      >
        {(() => {
            const bottomSectionKeywords = [
              "Machine tonnage:",
              "Tonnage:",
              "Cycle time:",
              "Cycle Time(sec):",
              "Machine rate:",
              "OHP rate:",
              "No. of operations:",
              "Material price:",
              "Material Price:",
              "Labour cost:",
              "Labour Rate:",
              "Glue flap:",
              "Board cost per box:",
              "Ink cost per box:",
              "Material spec:",
              "Ink cost per_box:",
              "Total conversion cost:",
              "OHP:",
            ];

            const topAssumptions = assumptions.filter((assumption) => {
              const assumptionStr =
                typeof assumption === "string" ? assumption : String(assumption || "");
              return !bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase()),
              );
            });

            let bottomAssumptions = assumptions.filter((assumption) => {
              const assumptionStr =
                typeof assumption === "string" ? assumption : String(assumption || "");
              return bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase()),
              );
            });

            if (formulaResult) {
              const requiredTonnage =
                formulaResult.MachineTonnage?.required_ton ??
                formulaResult.MachineTonnage?.requiredTonnage_ton;
              if (requiredTonnage !== undefined && requiredTonnage !== null) {
                bottomAssumptions = bottomAssumptions.filter((assumption) => {
                  const assumptionStr =
                    typeof assumption === "string" ? assumption : String(assumption || "");
                  return (
                    !assumptionStr.toLowerCase().startsWith("machine tonnage:") &&
                    !assumptionStr.toLowerCase().startsWith("tonnage:")
                  );
                });
                bottomAssumptions.push(
                  `Machine tonnage: ${parseFloat(requiredTonnage).toFixed(2)} tons`,
                );
              }

              const totalCycleTime =
                formulaResult.CycleTime?.total_cycle_s ??
                formulaResult.CycleTime?.totalCycleTime_s;
              if (totalCycleTime !== undefined && totalCycleTime !== null) {
                bottomAssumptions = bottomAssumptions.filter((assumption) => {
                  const assumptionStr =
                    typeof assumption === "string" ? assumption : String(assumption || "");
                  return (
                    !assumptionStr.toLowerCase().startsWith("cycle time") &&
                    !assumptionStr.toLowerCase().startsWith("cycle time(sec):")
                  );
                });
                bottomAssumptions.push(`Cycle Time(sec): ${parseFloat(totalCycleTime).toFixed(2)}`);
              }
            }

            const buildProductInfo = () => {
              if (!productData) {
                return topAssumptions.map((assumption) => assumption);
              }

              const productInfo = [];

              if (productData.productName) {
                productInfo.push(`Product Name: ${productData.productName}`);
              }
              if (productData.materialSelection) {
                productInfo.push(`Material: ${productData.materialSelection}`);
              }
              if (productData.batchSize) {
                productInfo.push(`Batch Size: ${productData.batchSize}`);
              }

              const dims = productData.partWeightDimensions;
              if (dims) {
                const dimParts = [];
                if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
                if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
                if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
                if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                if (dimParts.length > 0) {
                  productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                }
              }

              const details = productData.partDetails;
              if (details) {
                const detailParts = [];
                if (details.noOfCavities !== undefined && details.noOfCavities !== null) {
                  detailParts.push(`Cavities: ${details.noOfCavities}`);
                }
                if (details.regrindPct !== undefined && details.regrindPct !== null) {
                  detailParts.push(`Regrind: ${details.regrindPct}%`);
                }
                if (details.runnerType) {
                  detailParts.push(`Runner Type: ${details.runnerType}`);
                }
                const wallThickness =
                  details.wallThickness !== undefined && details.wallThickness !== null
                    ? details.wallThickness
                    : details.avgWallThickness !== undefined && details.avgWallThickness !== null
                      ? details.avgWallThickness
                      : null;
                if (wallThickness !== null) {
                  detailParts.push(`Wall Thickness: ${wallThickness}mm`);
                }
                if (detailParts.length > 0) {
                  productInfo.push(`Part Details: ${detailParts.join(", ")}`);
                }
              }

              if (productData.manufacturingCountry) {
                productInfo.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
              }

              return productInfo;
            };

            const productInfoItems = buildProductInfo();

            return (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: assumptionColors.textPrimary,
                    }}
                  >
                    Product Information
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {productInfoItems.map((item, idx) => {
                      const itemStr =
                        typeof item === "string"
                          ? item
                          : typeof item === "object" && item !== null
                            ? item.value || item.name || item.reason || JSON.stringify(item)
                            : String(item || "");
                      return (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: assumptionColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {itemStr}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {bottomAssumptions.length > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      bgcolor: assumptionColors.border || "rgba(255, 255, 255, 0.12)",
                      alignSelf: "stretch",
                    }}
                  />
                )}

                {bottomAssumptions.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: perlTypography.family,
                        letterSpacing: "0.3px",
                        color: assumptionColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {bottomAssumptions.map((assumption, idx) => {
                        const assumptionStr =
                          typeof assumption === "string"
                            ? assumption
                            : typeof assumption === "object" && assumption !== null
                              ? assumption.value ||
                                assumption.name ||
                                assumption.reason ||
                                JSON.stringify(assumption)
                              : String(assumption || "");
                        return (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: assumptionColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {assumptionStr}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
      </Paper>
    </Box>
  );
}

const extractArrayFromUnknown = (value, preferredKeys = []) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      return extractArrayFromUnknown(parsed, preferredKeys);
    } catch {
      return [];
    }
  }

  if (value && typeof value === "object") {
    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const extracted = extractArrayFromUnknown(value[key], preferredKeys);
        if (extracted.length > 0) return extracted;
      }
    }

    for (const nested of Object.values(value)) {
      const extracted = extractArrayFromUnknown(nested, preferredKeys);
      if (extracted.length > 0) return extracted;
    }
  }

  return [];
};

const findNestedValueByKey = (value, targetKey) => {
  if (!value || typeof value !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(value, targetKey)) {
    return value[targetKey];
  }

  for (const nested of Object.values(value)) {
    const result = findNestedValueByKey(nested, targetKey);
    if (result !== undefined && result !== null) return result;
  }

  return undefined;
};

const findNestedFxRate = (value) => {
  if (!value || typeof value !== "object") return undefined;

  const fxRate = value.fxRate_used_if_applicable;
  if (fxRate && typeof fxRate === "object" && fxRate.rate !== undefined) {
    return fxRate.rate;
  }

  for (const nested of Object.values(value)) {
    const result = findNestedFxRate(nested);
    if (result !== undefined && result !== null) return result;
  }

  return undefined;
};

const normalizeLookupKey = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const getValueByAnyKey = (source, keys = []) => {
  if (!source || typeof source !== "object") return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }

  const normalizedEntries = Object.entries(source).reduce((acc, [key, value]) => {
    acc[normalizeLookupKey(key)] = value;
    return acc;
  }, {});

  for (const key of keys) {
    const normalizedKey = normalizeLookupKey(key);
    if (Object.prototype.hasOwnProperty.call(normalizedEntries, normalizedKey)) {
      return normalizedEntries[normalizedKey];
    }
  }

  return undefined;
};

const parseCostNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }
  const cleaned = String(value).replace(/[$,\s]/g, "");
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parsePositiveNumber = (value, fallback = null) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const buildCostDataFromBomJson = (bomRaw) => {
  const bomRows = extractArrayFromUnknown(bomRaw, ["BOMJson", "bom", "rows", "data"]);
  const bomRow = bomRows.find((row) => row && typeof row === "object");
  if (!bomRow) return null;

  const costFields = [
    {
      key: "material",
      sourceKeys: ["MaterialCost", "Material_Cost", "material_cost", "material"],
    },
    {
      key: "process",
      sourceKeys: ["ProcessCost", "Process_Cost", "process_cost", "process"],
    },
    {
      key: "ohp",
      sourceKeys: [
        "OHPCost",
        "OHP_Cost",
        "ohp_cost",
        "OHP",
        "OverheadsAndProfitCost",
        "Overheads_And_Profit_Cost",
      ],
    },
  ];

  const breakdown = costFields.reduce((acc, field) => {
    const rawValue = getValueByAnyKey(bomRow, field.sourceKeys);
    const parsedValue = parseCostNumber(rawValue);
    if (parsedValue !== null) {
      acc[field.key] = parsedValue;
    }
    return acc;
  }, {});

  const rowTotal = parseCostNumber(
    getValueByAnyKey(bomRow, [
      "ShouldCost",
      "Should_Cost",
      "TotalCost",
      "Total_Cost",
      "TotalShouldCost",
      "TotalCostPerPart_usd",
    ]),
  );
  const calculatedTotal = Object.values(breakdown).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const total = rowTotal !== null ? rowTotal : calculatedTotal;

  if (Object.keys(breakdown).length === 0 && rowTotal === null) return null;

  return {
    breakdown: {
      ...breakdown,
      total_should_cost: total,
      currency: "USD/unit",
    },
    total,
    row: bomRow,
    rows: bomRows,
  };
};

const normalizeRunnerType = (value) => {
  const text = String(value || "").trim();
  if (!text) return null;
  const normalized = text.toLowerCase().replace(/[_-]/g, " ");
  if (normalized.includes("hot")) return "Hot Runner";
  if (normalized.includes("cold")) return "Cold Runner";
  if (normalized === "n/a" || normalized === "na") return "N/A";
  return text;
};

const enrichPlasticsPlayBookData = ({ playbookRaw, productData, aiLookupSource }) => {
  const source = playbookRaw?.playBookData || playbookRaw || {};
  const existingCostParams = source.costParams || {};
  const productDetails = productData?.partDetails || {};

  const cavities =
    parsePositiveNumber(existingCostParams.cavities) ??
    parsePositiveNumber(source.cavities) ??
    parsePositiveNumber(productDetails.noOfCavities) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "noOfCavities")) ??
    0;
  const cycleTime =
    parsePositiveNumber(existingCostParams.cycleTime) ??
    parsePositiveNumber(source.cycleTime) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "totalCycleTime_s")) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "total_cycle_s")) ??
    0;
  const machineTonnage =
    parsePositiveNumber(existingCostParams.machineTonnage) ??
    parsePositiveNumber(source.machineTonnage) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "selectedMachineTonnage_ton")) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "requiredTonnage_ton")) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "required_ton")) ??
    0;
  const ohpRate =
    parsePositiveNumber(existingCostParams.ohpRate) ??
    parsePositiveNumber(source.ohpRate) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "ohpRate")) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "overhead_pct_of_mfg_process")) ??
    parsePositiveNumber(findNestedValueByKey(aiLookupSource, "overhead_pct_of_mfgProcess")) ??
    0;
  const runnerType =
    normalizeRunnerType(existingCostParams.runnerType) ||
    normalizeRunnerType(source.runnerType) ||
    normalizeRunnerType(productDetails.runnerType) ||
    normalizeRunnerType(findNestedValueByKey(aiLookupSource, "runnerType")) ||
    normalizeRunnerType(findNestedValueByKey(aiLookupSource, "runner_type")) ||
    "N/A";

  return {
    ...source,
    costParams: {
      ...existingCostParams,
      cavities,
      cycleTime,
      machineTonnage,
      ohpRate,
      runnerType,
    },
  };
};

const shouldExcludeFrontendSchema = (schemaType) => {
  const value = String(schemaType || "")
    .trim()
    .toLowerCase();
  return (
    value === "derivedschemadefinitions" ||
    value === "derivedschemadefinition" ||
    value === "derivedschemadefinitionsjson" ||
    value === "derivedschemadefinitionjson" ||
    value === "derived_schema_definitions"
  );
};

const createGuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return Math.floor(value).toString(16);
  });
};

export default function GenericCostingPage2() {
  const [searchParams] = useSearchParams();
  const tower = searchParams.get("tower") || "MECH";
  const commodityParam = searchParams.get("commodity") || "PLASTICS";
  const commodity = commodityParam === "PLASTICS" ? "PLASTICS" : commodityParam;
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    surface: "#1e1f20",
    border: "rgba(255,255,255,0.12)",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A8AB",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};
  const towerConfig = useMemo(() => getTowerConfig(tower), [tower]);

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    playbook: false,
    results: false,
  });
  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [productImageError, setProductImageError] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [assumptionsData, setAssumptionsData] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const [_frontendSchemaResults, setFrontendSchemaResults] = useState({});
  const [_schemaTypes, setSchemaTypes] = useState([]);
  const [_schemaDefinitionColumns, setSchemaDefinitionColumns] = useState([]);
  const [_schemaDefinitions, setSchemaDefinitions] = useState([]);
  const [_frontendReturnItems, setFrontendReturnItems] = useState([]);
  const [_requestPayload, setRequestPayload] = useState(null);
  const [_statusMessage, setStatusMessage] = useState("");
  const previousRunKeyRef = React.useRef(null);
  const lastProductInputRef = React.useRef(null);
  const quoteRequestGenerationRef = React.useRef(0);
  const imageRequestGenerationRef = React.useRef(0);
  React.useEffect(() => () => {
    imageRequestGenerationRef.current += 1;
    quoteRequestGenerationRef.current += 1;
  }, []);
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  const setNormalizedProductData = useCallback((data) => {
    const nextProductData = data ? normalizePlasticsProductData(data) : null;
    if (areProductDataEqual(lastProductInputRef.current, nextProductData)) return;
    lastProductInputRef.current = nextProductData;
    quoteRequestGenerationRef.current += 1;
    imageRequestGenerationRef.current += 1;
    setCostData(null);
    setPlayBookData(null);
    setProductImageUrl(null);
    setProductImageError("");
    setRiskData(null);
    setSubstituteData(null);
    setAssumptionsData(null);
    setKeyInsightsData(null);
    setGeneratingImage(false);
    setIsProcessing(false);
    setShowProgressTracker(false);
    setProductData((prev) =>
      areProductDataEqual(prev, nextProductData) ? prev : nextProductData,
    );
  }, []);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, product: true }));
  };

  const handleProductComplete = useCallback(
    (data) => {
      setNormalizedProductData(data);
    },
    [setNormalizedProductData],
  );

  const handleProductDataChange = useCallback(
    (data) => {
      setNormalizedProductData(data);
    },
    [setNormalizedProductData],
  );

  const handlePlayBookChange = useCallback((field, value) => {
    setPlayBookData((prev) => ({ ...(prev || {}), [field]: value }));
    quoteRequestGenerationRef.current += 1;
    imageRequestGenerationRef.current += 1;
    setCostData(null);
    setProductImageUrl(null);
    setProductImageError("");
    setRiskData(null);
    setSubstituteData(null);
    setAssumptionsData(null);
    setKeyInsightsData(null);
    setGeneratingImage(false);
    setIsProcessing(false);
    setShowProgressTracker(false);
  }, []);

  const runDeferredImageGeneration = async (_basePayload, completeResponse) => {
    const requestGeneration = ++imageRequestGenerationRef.current;
    const responseProductData =
      completeResponse?.productData && typeof completeResponse.productData === "object"
        ? completeResponse.productData
        : null;
    const product =
      responseProductData ||
      (productData && typeof productData === "object" ? productData : null);

    if (!product) {
      console.warn("Cannot generate image: product data is missing");
      return;
    }

    setGeneratingImage(true);
    setProductImageUrl(null);
    setProductImageError("");
    try {
      const imagePayload = {
        tower,
        commodity,
        payload_inputs: {
          ...(_basePayload?.payload_inputs || {}),
          productData: product,
          productName: product.productName || "",
          materialSelection:
            product.materialSelection?.material ||
            product.materialSelection?.name ||
            product.materialSelection || "",
          manufacturingCountry: product.manufacturingCountry || "",
          country: product.manufacturingCountry || "",
          batchSize: product.batchSize || "",
          partWeightDimensions: product.partWeightDimensions || {},
          partDetails: product.partDetails || {},
        },
      };

      if (!imagePayload.payload_inputs.productName) {
        throw new Error("Product name is required to generate an image.");
      }

      // Only this application endpoint is permitted; a response cannot direct
      // authenticated API requests to an arbitrary address.
      const defaultEndpoint = "/api/mech/plastics/generic2/quick-cost-image";
      const imageEndpoint = completeResponse?.imageGenerationEndpoint === defaultEndpoint
        ? completeResponse.imageGenerationEndpoint : defaultEndpoint;
      const imageResponse = await apiFetch(imageEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      const imageData = await imageResponse.json().catch(() => ({}));
      if (requestGeneration !== imageRequestGenerationRef.current) return;
      if (!imageResponse.ok || imageData?.status !== "success") {
        throw new Error(imageData?.message || imageData?.productImage?.error || "Product image generation failed.");
      }

      const imageUrl = imageData?.productImage?.imageUrl;
      if (typeof imageUrl !== "string" ||
          !(/^(?:data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$|https:\/\/)/.test(imageUrl))) {
        throw new Error("Image service returned no usable product image.");
      }
      setProductImageUrl(imageUrl);
    } catch (error) {
      if (requestGeneration !== imageRequestGenerationRef.current) return;
      console.error("Deferred product image generation failed:", error);
      setProductImageError(error?.message || "Product image generation failed.");
      setStatusMessage((prev) =>
        prev
          ? `${prev} | Image generation failed: ${error?.message || "Unknown error"}`
          : `Image generation failed: ${error?.message || "Unknown error"}`,
      );
    } finally {
      if (requestGeneration === imageRequestGenerationRef.current) setGeneratingImage(false);
    }
  };

  const generateProductImage = () => runDeferredImageGeneration(null, { productData });

  const applyQuickCostResponse = (data) => {
    // Only the backend-validated quote may determine displayed pricing.
    // Independently generated BOM exports are retained for reference.
    const validatedQuote = readValidatedPlasticsQuote(data);
    const fetchedSchemaTypes = Array.isArray(data?.schema_types) ? data.schema_types : [];
    const fetchedSchemaDefinitionColumns = Array.isArray(data?.schema_definition_columns)
      ? data.schema_definition_columns
      : [];
    const fetchedSchemaDefinitions = Array.isArray(data?.schema_definitions)
      ? data.schema_definitions
      : [];
    const fetchedFrontendReturnItemsRaw = Array.isArray(data?.frontend_return_items)
      ? data.frontend_return_items
      : [];
    const fetchedFrontendReturnItems = fetchedFrontendReturnItemsRaw.filter(
      (item) => !shouldExcludeFrontendSchema(item?.schema_type),
    );
    const schemaAiResults =
      data?.schema_ai_results && typeof data.schema_ai_results === "object"
        ? data.schema_ai_results
        : {};
    const schemaResultsMap = fetchedFrontendReturnItems.reduce((acc, item) => {
      if (item?.schema_type) {
        acc[item.schema_type] = item.result;
      }
      return acc;
    }, { ...schemaAiResults });

    setSchemaTypes(fetchedSchemaTypes);
    setSchemaDefinitionColumns(fetchedSchemaDefinitionColumns);
    setSchemaDefinitions(fetchedSchemaDefinitions);
    setFrontendReturnItems(fetchedFrontendReturnItems);
    setFrontendSchemaResults(schemaResultsMap);

    const aiLookupSource = {
      ...schemaResultsMap,
      schema_ai_results: schemaAiResults,
      response: data,
    };

    const playbookRaw =
      schemaResultsMap.playBookData || schemaResultsMap.PlayBookData || data.playBookData;
    if (playbookRaw) {
      setPlayBookData(enrichPlasticsPlayBookData({ playbookRaw, productData, aiLookupSource }));
      setSectionStarted((prev) => ({ ...prev, playbook: true }));
    } else {
      const fallbackPlayBookData = enrichPlasticsPlayBookData({
        playbookRaw: {},
        productData,
        aiLookupSource,
      });
      const hasFallbackPlayBookValues = Object.values(fallbackPlayBookData.costParams || {}).some(
        (value) => value !== 0 && value !== "N/A",
      );
      if (hasFallbackPlayBookValues) {
        setPlayBookData(fallbackPlayBookData);
        setSectionStarted((prev) => ({ ...prev, playbook: true }));
      }
    }

    const riskRaw = schemaResultsMap.riskData || schemaResultsMap.RiskData || data.riskData;
    if (riskRaw) {
      setRiskData(riskRaw.riskData || riskRaw);
    }

    const substituteRaw =
      schemaResultsMap.substituteData || schemaResultsMap.SubstituteData || data.substituteData;
    if (substituteRaw) {
      setSubstituteData(substituteRaw.substituteData || substituteRaw);
    }

    const keyInsightsRaw = schemaResultsMap.KeyInsightsJson || data.KeyInsightsJson;
    if (keyInsightsRaw) {
      setKeyInsightsData(keyInsightsRaw.key_insights || keyInsightsRaw);
    }

    const imageRaw = schemaResultsMap.productImage || data.productImage;
    if (imageRaw?.imageUrl) {
      setProductImageUrl(imageRaw.imageUrl);
    }

    const bomRaw =
      schemaResultsMap.BOMJson ||
      schemaResultsMap.bomJson ||
      schemaResultsMap.BOM ||
      data.BOMJson ||
      data.bomJson ||
      data.BOM;
    const bomCostData = buildCostDataFromBomJson(bomRaw);
    const shouldCostRaw = schemaResultsMap.ShouldCostResult || data.ShouldCostResult;
    const assumptionsRaw =
      schemaResultsMap.AssumptionsJson ||
      data.AssumptionsJson ||
      schemaResultsMap.assumptions ||
      data.assumptions;
    const formulaResult = schemaResultsMap.formulaResult || data.formulaResult || null;
    const aiRequiredTonnage =
      parsePositiveNumber(findNestedValueByKey(aiLookupSource, "requiredTonnage_ton")) ??
      parsePositiveNumber(findNestedValueByKey(aiLookupSource, "required_ton"));
    const aiCycleTimeSeconds =
      parsePositiveNumber(findNestedValueByKey(aiLookupSource, "totalCycleTime_s")) ??
      parsePositiveNumber(findNestedValueByKey(aiLookupSource, "total_cycle_s"));
    const aiMaterialRate = parsePositiveNumber(findNestedFxRate(aiLookupSource));

    let materialPricePerKg = null;
    if (data?.materialPrice_usd_per_kg !== undefined && data?.materialPrice_usd_per_kg !== null) {
      materialPricePerKg = parseFloat(data.materialPrice_usd_per_kg) || null;
    } else if (assumptionsRaw?.MaterialMaster?.price_usd_per_kg !== undefined) {
      materialPricePerKg = parseFloat(assumptionsRaw.MaterialMaster.price_usd_per_kg) || null;
    } else if (assumptionsRaw?.MaterialMaster?.fxRate_used_if_applicable?.rate !== undefined) {
      materialPricePerKg =
        parseFloat(assumptionsRaw.MaterialMaster.fxRate_used_if_applicable.rate) || null;
    } else if (data?.MaterialMaster?.price_usd_per_kg !== undefined) {
      materialPricePerKg = parseFloat(data.MaterialMaster.price_usd_per_kg) || null;
    } else if (data?.MaterialMaster?.fxRate_used_if_applicable?.rate !== undefined) {
      materialPricePerKg = parseFloat(data.MaterialMaster.fxRate_used_if_applicable.rate) || null;
    } else if (aiMaterialRate !== null) {
      materialPricePerKg = aiMaterialRate;
    }

    let cycleTimeSeconds = 0;
    if (data?.cycleTime_s !== undefined && data?.cycleTime_s !== null) {
      cycleTimeSeconds = parseFloat(data.cycleTime_s) || 0;
    } else if (formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
      cycleTimeSeconds = parseFloat(formulaResult.CycleTime.totalCycleTime_s) || 0;
    } else if (formulaResult?.CycleTime?.total_cycle_s !== undefined) {
      cycleTimeSeconds = parseFloat(formulaResult.CycleTime.total_cycle_s) || 0;
    } else if (assumptionsRaw?.CycleTime?.totalCycleTime_s !== undefined) {
      cycleTimeSeconds = parseFloat(assumptionsRaw.CycleTime.totalCycleTime_s) || 0;
    } else if (assumptionsRaw?.CycleTime?.total_cycle_s !== undefined) {
      cycleTimeSeconds = parseFloat(assumptionsRaw.CycleTime.total_cycle_s) || 0;
    } else if (aiCycleTimeSeconds !== null) {
      cycleTimeSeconds = aiCycleTimeSeconds;
    }

    const formulaResultWithAiFallbacks = {
      ...(formulaResult || {}),
      MachineTonnage: {
        ...(formulaResult?.MachineTonnage || {}),
        ...(assumptionsRaw?.MachineTonnage || {}),
        ...(data?.MachineTonnage || {}),
        ...(aiRequiredTonnage !== null ? { requiredTonnage_ton: aiRequiredTonnage } : {}),
      },
      CycleTime: {
        ...(formulaResult?.CycleTime || {}),
        ...(assumptionsRaw?.CycleTime || {}),
        ...(data?.CycleTime || {}),
        ...(aiCycleTimeSeconds !== null ? { totalCycleTime_s: aiCycleTimeSeconds } : {}),
      },
    };

    let machineRatePerHour = null;
    if (data?.machineRate_usd_per_hr !== undefined && data?.machineRate_usd_per_hr !== null) {
      machineRatePerHour = parseFloat(data.machineRate_usd_per_hr) || null;
    } else if (assumptionsRaw?.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
      machineRatePerHour =
        parseFloat(assumptionsRaw.CostingAssumptions.machineRate_usd_per_hr) || null;
    } else if (data?.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
      machineRatePerHour = parseFloat(data.CostingAssumptions.machineRate_usd_per_hr) || null;
    }

    const normalizedAssumptions = buildAssumptionsArray({
      assumptionsRaw,
      productData,
      formulaResult: formulaResultWithAiFallbacks,
      materialPricePerKg,
      cycleTimeSeconds,
      machineRatePerHour,
    });
    if (normalizedAssumptions.length > 0) {
      setAssumptionsData(normalizedAssumptions);
    }

    setCostData({
      ...validatedQuote,
      assumptions: normalizedAssumptions,
      formulaResult: formulaResultWithAiFallbacks,
      materialPrice_usd_per_kg: materialPricePerKg,
      cycleTime_s: cycleTimeSeconds,
      bomRow: bomCostData?.row || null,
      bomRows: bomCostData?.rows || [],
      _source: "ShouldCostResult",
    });

    setStatusMessage(
      `Fetched ${fetchedSchemaTypes.length} schema types for ${tower}/${commodity}.`,
    );
    
    const hasResultData =
      !!shouldCostRaw ||
      !!bomCostData ||
      !!bomRaw ||
      !!riskRaw ||
      !!substituteRaw ||
      !!keyInsightsRaw ||
      !!assumptionsRaw ||
      !!imageRaw ||
      !!playbookRaw;

    setSectionStarted((prev) => ({
      ...prev,
      playbook: prev.playbook || !!playbookRaw,
      results: prev.results || hasResultData,
    }));
  };

  const handleQuickCost = async () => {
    if (!productData) {
      alert("Please complete product information first");
      return;
    }

    const normalizedProductData = normalizePlasticsProductData(productData);
    const requestGeneration = ++quoteRequestGenerationRef.current;
    const params = playBookData?.costParams || {};
    const plasticsOverrides = Object.fromEntries(
      ["cavities", "cycleTime", "machineTonnage", "ohpRate", "runnerType"]
        .filter((key) => params._overrides?.[key] === true)
        .map((key) => [key, params[key]]),
    );
    const requestedPartDetails = { ...(normalizedProductData.partDetails || {}) };
    if (params._overrides?.cavities === false) requestedPartDetails.noOfCavities = "";
    if (params._overrides?.machineTonnage === false) requestedPartDetails.machineTonnage = "";
    normalizedProductData.partDetails = requestedPartDetails;
    const payloadBase = {
      tower,
      commodity,
      productData: normalizedProductData,
      productName: normalizedProductData.productName || "",
      manufacturingCountry: normalizedProductData.manufacturingCountry || "",
      country: normalizedProductData.manufacturingCountry || "",
      batchSize: normalizedProductData.batchSize || "",
      materialSelection: normalizedProductData.materialSelection || "",
      partWeightDimensions: normalizedProductData.partWeightDimensions || {},
      partDetails: requestedPartDetails,
      plasticsOverrides,
    };
    const runKeyString = JSON.stringify(payloadBase);
    const isReRun = previousRunKeyRef.current === runKeyString;
    const primaryPackaging =
      playBookData?.primary_packaging || playBookData?.primaryPackaging || "";

    setIsProcessing(true);
    setStatusMessage("");
    if (!isReRun && Object.keys(plasticsOverrides).length === 0) {
      setPlayBookData(null);
    }
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    imageRequestGenerationRef.current += 1;
    setProductImageUrl(null);
    setProductImageError("");
    setGeneratingImage(false);
    setAssumptionsData(null);
    setKeyInsightsData(null);
    setFrontendSchemaResults({});
    setSchemaDefinitionColumns([]);
    setSchemaDefinitions([]);
    setFrontendReturnItems([]);
    setProgressSteps([]);
    setCurrentProgressStep(0);
    setOverallProgress(0);
    setShowProgressTracker(true);

    try {
      const metadataPayload = {
        userEmail: user?.email || "",
        projectId: createGuid(),
        partKey: `PART_${Date.now()}`,
        componentId: "Comp1",
        isReRun,
      };

      const payloadInputs = {
        ...payloadBase,
        primaryPackaging: primaryPackaging || undefined,
      };

      const payload = {
        tower,
        commodity,
        metadata: metadataPayload,
        payload_inputs: payloadInputs,
        primaryPackaging: primaryPackaging || undefined,
      };

      setRequestPayload(payload);

      const response = await apiFetch("/api/mech/plastics/generic2/quick-cost-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Streaming response is not available");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let completed = false;
      let streamTotalSteps = 1;
      const successfulSteps = new Set();

      while (!completed) {
        const { done, value } = await reader.read();
        if (requestGeneration !== quoteRequestGenerationRef.current) {
          await reader.cancel?.();
          return;
        }
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          const eventData = JSON.parse(dataMatch[1]);
          if (eventData.type === "start") {
            const totalSteps = eventData.total_steps || 1;
            streamTotalSteps = totalSteps;
            successfulSteps.clear();
            const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
              id: `step-${i + 1}`,
              label: `Step ${i + 1}`,
              status: "pending",
              message: "",
            }));
            setProgressSteps(initialSteps);
            setCurrentProgressStep(1);
            continue;
          }

          if (eventData.type === "progress") {
            const step = Number(eventData.step) || 1;
            const total = Number(eventData.total) || 1;
            const status = String(eventData.status || "").toLowerCase();
            const mappedStatus =
              status === "running"
                ? "in-progress"
                : status === "success"
                  ? "completed"
                  : status === "skipped"
                    ? "completed"
                    : status === "error"
                      ? "error"
                      : status;

            setProgressSteps((prev) => {
              const updated = [...prev];
              const idx = step - 1;
              if (updated[idx]) {
                updated[idx] = {
                  ...updated[idx],
                  label: eventData.label || updated[idx].label,
                  status: mappedStatus,
                  message: eventData.message || "",
                  error: eventData.error,
                };
              }
              return updated;
            });

            if (status === "running" || status === "success" || status === "skipped") {
              setCurrentProgressStep(step);
            }

            if (status === "success" || status === "skipped") {
              successfulSteps.add(step);
              const pct = (successfulSteps.size / total) * 100;
              setOverallProgress((prev) => (pct > prev ? pct : prev));
            }
            continue;
          }

          if (eventData.type === "complete") {
            completed = true;
            if (eventData.status !== "success") {
              const detail = eventData.error?.details?.[0]?.message;
              const runReference = eventData.runId ? `Run ID: ${eventData.runId}.` : "";
              throw new Error([eventData.message || "Quick Cost failed", detail, runReference]
                .filter(Boolean).join(" "));
            }

            applyQuickCostResponse(eventData);
            // Preserve explicit intent across a successful rerun; generated
            // values in the remaining fields stay automatic.
            setPlayBookData((prev) => ({ ...(prev || {}), costParams: {
              ...(prev?.costParams || {}), ...plasticsOverrides,
              _overrides: { ...(params._overrides || {}) },
            } }));
            previousRunKeyRef.current = runKeyString;
            const finalPct = (successfulSteps.size / streamTotalSteps) * 100;
            setOverallProgress((prev) => (finalPct > prev ? finalPct : prev));
            setProgressSteps((prev) =>
              prev.map((item) => ({
                ...item,
                status: item.status === "error" ? "error" : "completed",
                message: item.status === "error" ? item.message || "Failed" : "Complete",
              })),
            );
            const payloadForImage = { ...payload };
            const completeResponse = eventData;
            setTimeout(() => {
              if (requestGeneration !== quoteRequestGenerationRef.current) return;
              setShowProgressTracker(false);
              setActiveSection("results");
              runDeferredImageGeneration(payloadForImage, completeResponse);
            }, 1200);
          }
        }
      }
      if (!completed) {
        throw new Error("Plastics costing stream ended before a complete quote was returned.");
      }
    } catch (error) {
      if (requestGeneration !== quoteRequestGenerationRef.current) return;
      setCostData(null);
      const message = error?.message || "Failed to call backend";
      setStatusMessage(`Quick Cost failed: ${message}`);
      alert(`Quick Cost failed: ${message}`);
      setShowProgressTracker(false);
    } finally {
      if (requestGeneration === quoteRequestGenerationRef.current) setIsProcessing(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description: `Answer a few quick questions about your ${commodity.toLowerCase()} product to run a quick backend schema query.`,
    },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product,
      title: "Ready to Configure PlayBook",
      description: "Placeholder section for playbook output.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product,
      title: "Analysis Report",
      description: "Shows schema types returned by plastics_generic_routes2.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total ?? costData?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  const hasResultsData =
    !!costData ||
    !!riskData ||
    !!substituteData ||
    !!productImageUrl ||
    !!assumptionsData ||
    !!keyInsightsData;

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Plastics Molding"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {sectionStarted.product && activeSection === "product" && (
              <Box sx={{ height: "92%" }}>
                <PlasticsQuestions
                  onComplete={handleProductComplete}
                  onDataChange={handleProductDataChange}
                  initialData={productData}
                  commodity={commodity}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <PlasticsPlayBook
                    data={playBookData}
                    onChange={handlePlayBookChange}
                    tower={tower}
                    commodity={commodity}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    No playbook output returned yet.
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {hasResultsData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    imageError={productImageError}
                    onRetryImage={generateProductImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor={towerConfig.brandColor}
                    assumptions={assumptionsData || []}
                    keyInsights={keyInsightsData || null}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    noCostModelMessage={
                      !costData
                        ? "AI analysis completed, but detailed should-cost calculations are not generated for plastics in this flow. You can still review the image, assumptions, risk analysis, and substitute recommendations."
                        : null
                    }
                    renderedAssumptions={renderAssumptions({
                      assumptions: assumptionsData || [],
                      productData,
                      formulaResult: costData?.formulaResult,
                    })}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        </Box>

        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor={towerConfig.brandColor}
          />
        )}

        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message="Querying schema types..."
          subtext={`${tower} / ${commodity}`}
        />
      </Box>
    </SidebarLayout>
  );
}
